import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Wrench, Plus, Calendar, AlertTriangle, FileText, Upload,
  Loader2, CheckCircle, Clock, Car
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { formatCurrency, formatDate } from "../../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MAINTENANCE_TYPES = [
  { value: "oil_change", label: "Vidange" },
  { value: "service", label: "Révision" },
  { value: "tire_change", label: "Changement pneus" },
  { value: "brake", label: "Freins" },
  { value: "technical_control", label: "Contrôle technique" },
  { value: "repair", label: "Réparation" },
  { value: "bodywork", label: "Carrosserie" },
  { value: "other", label: "Autre" },
];

export default function MaintenanceTab({ vehicleId, currentMileage }) {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [nextMaint, setNextMaint] = useState(null);
  const [tcAlert, setTcAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    maintenance_type: "service",
    date: new Date().toISOString().split("T")[0],
    mileage: currentMileage || "",
    description: "",
    supplier: "",
    amount_ht: "",
    amount_ttc: "",
    next_maintenance_km: "",
    next_maintenance_date: "",
  });
  
  const token = localStorage.getItem("token");

  const fetchMaintenance = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${vehicleId}/maintenance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(response.data.records || []);
      setNextMaint(response.data.next_maintenance);
      setTcAlert(response.data.technical_control_alert);
    } catch (err) {
      console.error("Error fetching maintenance:", err);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [vehicleId, token]);

  useEffect(() => {
    fetchMaintenance();
  }, [fetchMaintenance]);

  useEffect(() => {
    if (currentMileage && !form.mileage) {
      setForm(f => ({ ...f, mileage: currentMileage }));
    }
  }, [currentMileage]);

  const handleSubmit = async () => {
    if (!form.mileage || !form.supplier) {
      toast.error("Kilométrage et fournisseur requis");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/fleet/vehicles/${vehicleId}/maintenance`,
        {
          ...form,
          mileage: parseInt(form.mileage),
          amount_ht: form.amount_ht ? parseFloat(form.amount_ht) : null,
          amount_ttc: form.amount_ttc ? parseFloat(form.amount_ttc) : null,
          next_maintenance_km: form.next_maintenance_km ? parseInt(form.next_maintenance_km) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Entretien enregistré");
      setShowModal(false);
      setForm({
        maintenance_type: "service",
        date: new Date().toISOString().split("T")[0],
        mileage: currentMileage || "",
        description: "",
        supplier: "",
        amount_ht: "",
        amount_ttc: "",
        next_maintenance_km: "",
        next_maintenance_date: "",
      });
      fetchMaintenance();
    } catch (err) {
      console.error("Error creating maintenance:", err);
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Entretien</h3>
          <p className="text-sm text-slate-500">
            Historique et planification des entretiens
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="add-maintenance-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un entretien
        </Button>
      </div>

      {/* Alerts */}
      {tcAlert && (
        <Card className={tcAlert.priority === "critical" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${tcAlert.priority === "critical" ? "text-red-600" : "text-amber-600"}`} />
              <div>
                <p className={`font-medium ${tcAlert.priority === "critical" ? "text-red-800" : "text-amber-800"}`}>
                  Contrôle technique à prévoir
                </p>
                <p className={`text-sm ${tcAlert.priority === "critical" ? "text-red-700" : "text-amber-700"}`}>
                  Échéance le {formatDate(tcAlert.due_date)} (J-{tcAlert.days_remaining})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next maintenance */}
      {nextMaint && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Prochain entretien prévu</p>
                <p className="text-sm text-blue-700">
                  {nextMaint.type}
                  {nextMaint.km && ` à ${nextMaint.km.toLocaleString("fr-FR")} km`}
                  {nextMaint.date && ` ou le ${formatDate(nextMaint.date)}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total entretiens</p>
                <p className="text-2xl font-bold text-slate-900">{records.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Coût total</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(records.reduce((sum, r) => sum + (r.amount_ttc || 0), 0))}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Km actuel</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(currentMileage || 0).toLocaleString("fr-FR")}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Car className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Historique des entretiens</CardTitle>
        </CardHeader>
        <CardContent>
          {records.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Wrench className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p>Aucun entretien enregistré</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Kilométrage</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Prochain</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{formatDate(record.date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {record.maintenance_type_label}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">
                      {record.mileage?.toLocaleString("fr-FR")} km
                    </TableCell>
                    <TableCell>{record.supplier}</TableCell>
                    <TableCell className="text-right font-medium">
                      {record.amount_ttc ? formatCurrency(record.amount_ttc) : "-"}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {record.next_maintenance_km && `${record.next_maintenance_km.toLocaleString("fr-FR")} km`}
                      {record.next_maintenance_km && record.next_maintenance_date && " / "}
                      {record.next_maintenance_date && formatDate(record.next_maintenance_date)}
                      {!record.next_maintenance_km && !record.next_maintenance_date && "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Maintenance Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter un entretien</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type d'entretien *</Label>
                <Select
                  value={form.maintenance_type}
                  onValueChange={(v) => setForm({ ...form, maintenance_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kilométrage *</Label>
                <Input
                  type="number"
                  placeholder="Ex: 52000"
                  value={form.mileage}
                  onChange={(e) => setForm({ ...form, mileage: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fournisseur *</Label>
                <Input
                  placeholder="Ex: Norauto"
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Détails de l'intervention..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant HT</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount_ht}
                  onChange={(e) => setForm({ ...form, amount_ht: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Montant TTC</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount_ttc}
                  onChange={(e) => setForm({ ...form, amount_ttc: e.target.value })}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Prochain entretien prévu</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kilométrage</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 62000"
                    value={form.next_maintenance_km}
                    onChange={(e) => setForm({ ...form, next_maintenance_km: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>ou Date</Label>
                  <Input
                    type="date"
                    value={form.next_maintenance_date}
                    onChange={(e) => setForm({ ...form, next_maintenance_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Ajouter
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
